import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { AppLanguage } from '@/shared/config/i18n';

import { User, UserState } from './types';

const initialState: UserState = {
  data: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  deposit: {
    loading: false,
    error: null,
    lastTxHash: null,
  },
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    fetchUserRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchUserSuccess: (state, action: PayloadAction<User>) => {
      state.loading = false;
      state.data = action.payload;
      state.isAuthenticated = true;
      state.error = null;
    },
    fetchUserFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    },
    updateBalance: (state, action: PayloadAction<number>) => {
      if (state.data) {
        const w = state.data.wallets['TON'];
        if (w) w.balance += action.payload;
      }
    },
    updateBalanceByCurrency: (
      state,
      action: PayloadAction<{ currency: string; amount: number }>,
    ) => {
      if (!state.data) return;
      const currency = action.payload.currency.toUpperCase();
      if (!state.data.wallets[currency]) {
        state.data.wallets[currency] = { balance: 0, frozenBalance: 0, lockedBonus: 0 };
      }
      state.data.wallets[currency].balance += action.payload.amount;
    },
    depositRequest: (
      state,
      _action: PayloadAction<{ boc: string; amount: number; senderAddress: string }>,
    ) => {
      state.deposit.loading = true;
      state.deposit.error = null;
    },
    depositSuccess: (state, action: PayloadAction<{ newBalance: number; txHash: string }>) => {
      state.deposit.loading = false;
      state.deposit.lastTxHash = action.payload.txHash;
      if (state.data) {
        if (!state.data.wallets['TON']) {
          state.data.wallets['TON'] = { balance: 0, frozenBalance: 0, lockedBonus: 0 };
        }
        state.data.wallets['TON'].balance = action.payload.newBalance;
      }
    },
    depositFailure: (state, action: PayloadAction<string>) => {
      state.deposit.loading = false;
      state.deposit.error = action.payload;
    },
    // Optimistic update after withdraw submission (TON only for now)
    applyWithdraw: (state, action: PayloadAction<number>) => {
      if (state.data) {
        const w = state.data.wallets['TON'];
        if (w) {
          w.balance = Math.max(0, w.balance - action.payload);
          w.frozenBalance = (w.frozenBalance ?? 0) + action.payload;
        }
      }
    },
    // Called on WebSocket withdraw_update — syncs exact wallet balances per currency
    syncBalances: (
      state,
      action: PayloadAction<{
        currency: string;
        balance: number;
        frozenBalance: number;
        lockedBonus?: number;
      }>,
    ) => {
      if (state.data) {
        const previousLockedBonus = state.data.wallets[action.payload.currency]?.lockedBonus ?? 0;
        state.data.wallets[action.payload.currency] = {
          balance: action.payload.balance,
          frozenBalance: action.payload.frozenBalance,
          lockedBonus: action.payload.lockedBonus ?? previousLockedBonus,
        };
      }
    },
    updateLanguageRequest: (_state, _action: PayloadAction<AppLanguage>) => {},
    updateLanguageSuccess: (state, action: PayloadAction<AppLanguage>) => {
      if (state.data) state.data.language = action.payload;
    },
    logout: (state) => {
      state.data = null;
      state.isAuthenticated = false;
      state.error = null;
    },
  },
});

export const userActions = userSlice.actions;
export const userReducer = userSlice.reducer;
